import React from 'react';
import { ChangeTypeBadge } from './ChangeTypeBadge';
import { FieldContent } from './DiffFieldContent';

export interface FieldData {
  id: string;
  label: string;
  type: string;
  value?: any;
  mandatory?: boolean;
  translation?: boolean;
  multiple?: boolean;
  filter?: boolean;
  documentation?: string;
  selectedTypes?: string[];
  selectedTags?: string[];
  filterOptions?: string[];
  hiddenForRoles?: string[];
  readOnlyForRoles?: string[];
}



// Placeholder component for fields that don't exist or will be removed
export const PlaceholderContent: React.FC<{ message: string }> = ({ message }) => {
  return (
    <div className="flex items-start justify-start h-full min-h-0">
      <div className="text-xs text-gray-400 dark:text-gray-500 italic text-center">
        {message}
      </div>
    </div>
  );
};

// Component for unchanged fields
export const UnchangedFieldItem: React.FC<{ 
  field: FieldData;
  onFieldChange?: (fieldId: string, value: any) => void;
}> = ({ field, onFieldChange }) => {
  return (
    <div className="grid grid-cols-2 divide-x divide-gray-300 dark:divide-gray-600">
      {/* Left side - current version (view only) */}
      <div className="p-3 bg-white dark:bg-gray-900">
        <FieldContent field={field} mode="view" />
      </div>

      {/* Right side - editable version */}
      <div className="p-3 bg-white dark:bg-gray-900">
        <FieldContent field={field} mode="edit" onFieldChange={onFieldChange} />
      </div>
    </div>
  );
};

// Component for added fields
export const AddedFieldItem: React.FC<{ 
  field: FieldData;
  onFieldChange?: (fieldId: string, value: any) => void;
}> = ({ field, onFieldChange }) => {
  return (
    <div className="grid grid-cols-2 divide-x divide-gray-300 dark:divide-gray-600">
      {/* Left side - placeholder */}
      <div className="p-3 bg-gray-100 dark:bg-gray-800 opacity-30 flex items-center justify-center">
        <PlaceholderContent message="(field doesn't exist yet)" />
      </div>

      {/* Right side - new field (editable) */}
      <div className="p-3 bg-green-100 dark:bg-green-950/40">
        <div className="space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <FieldContent
                field={field}
                showPrefix={true}
                prefix="+"
                mode="edit"
                onFieldChange={onFieldChange}
              />
            </div>
            <ChangeTypeBadge type="added" size="sm" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Component for removed fields
export const RemovedFieldItem: React.FC<{ field: FieldData }> = ({ field }) => {
  // Check if field has a value that will be lost
  const hasValue = field.value !== undefined && field.value !== null && field.value !== "";

  return (
    <div className="grid grid-cols-2 divide-x divide-gray-300 dark:divide-gray-600">
      {/* Left side - removed field */}
      <div className="p-3 bg-red-100 dark:bg-red-950/40">
        <div className="space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              {hasValue && (
                <div className="mb-2 flex items-center gap-2 text-xs text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950/30 px-2 py-1 rounded">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span>Data will be lost</span>
                </div>
              )}
              <div className="opacity-60">
                <FieldContent
                  field={field}
                  showPrefix={true}
                  prefix="-"
                />
              </div>
            </div>
            <ChangeTypeBadge type="removed" size="sm" />
          </div>
        </div>
      </div>

      {/* Right side - placeholder */}
      <div className="p-3 bg-gray-100 dark:bg-gray-800 opacity-30 flex items-center justify-center">
        <PlaceholderContent message="(field will be removed)" />
      </div>
    </div>
  );
};

// Component for modified fields
export const ModifiedFieldItem: React.FC<{ 
  oldField: FieldData; 
  newField: FieldData;
  onFieldChange?: (fieldId: string, value: any) => void;
}> = ({
  oldField,
  newField,
  onFieldChange
}) => {
  // Check if both fields have the same value (data is being preserved)
  const hasValue = oldField.value !== undefined && oldField.value !== null && oldField.value !== "";
  const valuePreserved = hasValue && oldField.value === newField.value;

  return (
    <div className="grid grid-cols-2 divide-x divide-gray-300 dark:divide-gray-600">
      {/* Left side - old version (view only) */}
      <div className="p-3 bg-yellow-50 dark:bg-yellow-950/20">
        <div className="space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <FieldContent field={oldField} mode="view" />
            </div>
            <ChangeTypeBadge type="modified" size="sm" />
          </div>
        </div>
      </div>

      {/* Right side - new version (editable) */}
      <div className="p-3 bg-yellow-100 dark:bg-yellow-950/30">
        {valuePreserved && (
          <div className="mb-2 flex items-center gap-2 text-xs text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-950/20 px-2 py-1 rounded">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>Data will be preserved</span>
          </div>
        )}
        <FieldContent field={newField} mode="edit" onFieldChange={onFieldChange} />
      </div>
    </div>
  );
};

